import { useEffect, useRef, useState } from 'react';
import { useSpecialPermissions } from './useSpecialPermissions';

/**
 *
 * @param {Object} config
 * @param {Object} config.documentToAssign
 * @param {Function} config.onModalClose
 * @param {boolean} config.autoReloadOnClose
 *
 * @returns {Object}
 *
 * @example
 * const {
 *   isModalShowing,
 *   setIsModalShowing,
 *   shouldShowModal,
 *   documentToAssign,
 *   setDocumentToAssign
 * } = useDocumentAssignmentModal({
 *   onModalClose: () => console.log('Modal cerrado'),
 *   autoReloadOnClose: true
 * });
 */
export const useDocumentAssignmentModal = ({ onModalClose, autoReloadOnClose = false } = {}) => {
  const { canOmitMandatoryVisibility } = useSpecialPermissions();
  const [isModalShowing, setIsModalShowing] = useState(false);
  const [documentToAssign, setDocumentToAssign] = useState(null);
  const wasModalShowingRef = useRef(false);

  const hasDocumentToAssign = Boolean(documentToAssign && Object.keys(documentToAssign).length > 0);
  const shouldShowModal = hasDocumentToAssign;

  useEffect(() => {
    if (shouldShowModal && !isModalShowing) {
      setIsModalShowing(true);
    }
  }, [shouldShowModal, isModalShowing]);

  useEffect(() => {
    const wasModalShowing = wasModalShowingRef.current;

    if (wasModalShowing && !isModalShowing) {
      if (hasDocumentToAssign) {
        setDocumentToAssign(null);
      }

      if (onModalClose) {
        onModalClose();
      }

      if (autoReloadOnClose) {
        window.location.reload();
      }
    }

    wasModalShowingRef.current = isModalShowing;
  }, [isModalShowing, hasDocumentToAssign, onModalClose, autoReloadOnClose]);

  return {
    isModalShowing,
    setIsModalShowing,
    shouldShowModal,
    documentToAssign,
    setDocumentToAssign,
    canOmitMandatoryVisibility,
  };
};
